import React from 'react';
import { Box, LinearProgress, makeStyles } from '@material-ui/core';
import {
    FunctionField,
    List,
    Pagination,
    Record,
    ReferenceField,
    TextField as RaTextField,
    useDataProvider,
    useListContext,
    useNotify,
    useTranslate,
} from 'react-admin';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import AssignmentIcon from '@material-ui/icons/Assignment';
import DataGridCustom from '../UI/DataGrid';
import RightInfoDrawer from '../drawer/RightInfoDrawer';
import ActionList from './actions/';
import Filters from './filters/simpleListFilter';
import {
    AVATAR_ANCS,
    AVATARS,
    GROUP_METADATA_AVATAR_TYPES,
    METADATA_AVATAR_TYPES,
    YOURLS,
} from '../../providers/resources';
import { ancs } from '../../pages/paths';
import { useDispatch, useSelector } from 'react-redux';
import { selectAnc } from '../../redux/actions/ancs';
import { Avatar, GroupMetadataAvatarType, MC } from '../../utils/types';
import { getNameWithoutPrefix } from '../../utils/resources/MetadataAvatarType';
import { openBackofficeApp } from '../../utils/backofficeApp';
import InstallationDateAvatarPropertyReferenceField from '../avatarPropertyReferenceField/intallationDateAvatarPropertyReferenceField';
import InstallationDateField from '../avatarPropertyReferenceField/installationDateField';
import AvatarNoteReferenceField from '../avatarNoteReferenceField/avatarNoteReferenceField';
import ShowLastNoteIcon from '../showLastNoteIcon/showLastNoteIcon';
import { MAINTENANCE, SLUDGE_EMPTYING } from '../../utils/rikutec';
import { toFormatDate } from '../../utils/date';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import CreationDateField from './columns/CreationDateField';
import MetadataAvatarTypeProperties from './columns/MetadataAvatarTypeProperties';
import MetadataAvatarTypePropertiesBatched from './columns/MetadataAvatarTypePropertiesBatched';

const hasRoles = (roles: string[] = [], permissions: string[] = []) => {
    return roles.every(
        (role) =>
            -1 !==
            permissions.findIndex((prole) => prole.toLowerCase() === role.toLowerCase()),
    );
};

export interface EnabledColumns {
    processingCapacity: boolean;
    sludgeStorageVolume: boolean;
    totalUsefulVolume: boolean;
    currentState: boolean;
    model: boolean;
}

export type AvatarActions = 'ADD_VP' | 'EDIT_VP' | 'ADD_WC' | 'EDIT_WC' | 'NONE';

const useStyles = makeStyles(() => ({
    // Local styles for table view. Keep it minimal and scoped.
    container: {},
    checkbox: {},
    list: {},
}));

const ListPagination = (props: any) => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

interface Props {
    enabledColumns: EnabledColumns;
    toggleColumn: (column: string, enabled: boolean) => void;
}

const listProps = {
    resource: AVATARS,
    hasCreate: false,
    hasEdit: false,
    hasList: true,
    hasShow: false,
    basePath: ancs,
    sort: { field: 'creationDate', order: 'DESC' },
    perPage: 50,
    filter: {
        serialization_groups: [
            'read_meta',
            'read_mc',
            'read_simple_company',
            'read_avatar_current_node',
            'read_node',
            'avatar_files',
        ],
        'groupAvatars.alphaId': 'smartconnect:rikutec:ancs',
    },
};

const DateFieldWrapper: React.FC<{ record?: any; notes?: { [key: string]: any } }> = ({ notes }) => {
    const noteIds = notes && typeof notes === 'object' ? Object.keys(notes) : [];
    const noteId = noteIds.length > 0 ? noteIds[0] : null;
    return <>{noteId && notes ? toFormatDate(notes[noteId].createdAt) : ''}</>;
};

const LoadingBar = () => {
    const { loading } = useListContext();
    return loading ? (
        <LinearProgress style={{ position: 'absolute', top: -10, left: 0, right: 0, zIndex: 1000 }} />
    ) : null;
};

const TableView: React.FC<Props> = ({ enabledColumns, toggleColumn }) => {
    const classes = useStyles();
    const t = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const dispatch = useDispatch();
    const { permissions } = useRolePermissions();

    // Selected row state from Redux to control the right-side info panel
    const selected: any = useSelector((state: any) => state?.rikutec?.ancs?.selected || {});
    const panelOpen = selected && Object.keys(selected).length > 0;
    const handleClosePanel = () => dispatch(selectAnc({}));

    const dataGridProps = {
        rowClick: (id: string, basePath: string, record: Record) => {
            dispatch(selectAnc(record));
            return null;
        },
        toFindSelected: { resource: 'ancs', key: 'selected' },
        hasBulkActions: null,
        classes: {
            checkbox: classes.checkbox,
            row: classes.list,
        },
    };

    const getYourlsData = (avatar: Avatar, callback: (mcUrl: string) => void) => {
        if (avatar.mcs.length >= 1) {
            const mc: MC | string = avatar.mcs[0];
            if (typeof mc !== 'string' && 'yourls' in mc) {
                dataProvider
                    .getOne(YOURLS, { id: mc.yourls })
                    .then(({ data }) => {
                        if (data && 'mcUrl' in data && data.mcUrl) {
                            callback(data.mcUrl as string);
                        }
                    })
                    .catch(() => notify(t('app.ancs.error.server'), 'error'));
            }
        }
    };

    const simulateFingerMc = (avatar: Avatar) => {
        getYourlsData(avatar, (mcUrl) => window.open(mcUrl, '_blank', 'width=411px,height=731px'));
    };

    const openBackofficeForms = (avatar: Avatar) => {
        getYourlsData(avatar, (mcUrl) =>
            openBackofficeApp(mcUrl, 'forms', {
                onMissingRefreshToken: () => notify(t('app.ancs.backofficeMissingRefresh'), 'warning'),
            })
        );
    };

    return (
        <Box className='list'>
            <List
                component='div'
                {...listProps}
                pagination={<ListPagination />}
                filters={<Filters />}
                actions={<ActionList toggleColumn={toggleColumn} enabledColumns={enabledColumns} viewMode={'table'} />}
                bulkActionButtons={false}
                empty={false}
            >
                <Box
                    style={{
                        display: 'relative',
                    }}
                >
                    <LoadingBar />

                    <DataGridCustom {...dataGridProps}>
                        <ReferenceField
                            link={false}
                            sortable={true}
                            label={t('app.ancs.productTypology')}
                            reference={GROUP_METADATA_AVATAR_TYPES}
                            source='metadataAvatarType.groupMetadataAvatarType'
                            sortBy='metadataAvatarType.groupMetadataAvatarType.name'
                            basePath={AVATAR_ANCS}
                            resource={METADATA_AVATAR_TYPES}
                        >
                            <FunctionField
                                sortable={true}
                                // @ts-ignore
                                render={(record: GroupMetadataAvatarType) => `${getNameWithoutPrefix(record.name)}`}
                            />
                        </ReferenceField>

                        <FunctionField
                            label={t('app.ancs.articleNumber')}
                            // @ts-ignore
                            render={(record: Avatar) => {
                                const id =
                                    typeof record.metadataAvatarType === 'string'
                                        ? record.metadataAvatarType
                                        : record.metadataAvatarType.id;
                                return <MetadataAvatarTypePropertiesBatched metadataAvatarType={id} name='productCode' />;
                            }}
                        />

                        <FunctionField
                            sortable={true}
                            sortBy='metadataAvatarType.name'
                            label={t('app.ancs.metadataAvatarType.name')}
                            // @ts-ignore
                            render={(record: Avatar) => `${getNameWithoutPrefix(record.metadataAvatarType?.name)}`}
                        />

                        <RaTextField
                            sortable={true}
                            sortBy='alphaId'
                            label={t('app.ancs.serialNumber')}
                            source='alphaId'
                        />

                        <RaTextField label={t('app.ancs.postalCode')} source='postalCode' />

                        <FunctionField
                            label={t('app.objects.info.created')}
                            // @ts-ignore
                            render={(record: Avatar) => <CreationDateField record={record} />}
                        />

                        <InstallationDateAvatarPropertyReferenceField label={t('app.ancs.installingDate')}>
                            <InstallationDateField />
                        </InstallationDateAvatarPropertyReferenceField>

                        <AvatarNoteReferenceField
                            tags={[MAINTENANCE]}
                            componentIdentification={MAINTENANCE}
                            label={t('app.ancs.maintenanceLastDate')}
                        >
                            <DateFieldWrapper />
                        </AvatarNoteReferenceField>

                        <AvatarNoteReferenceField
                            tags={[SLUDGE_EMPTYING]}
                            componentIdentification={SLUDGE_EMPTYING}
                            label={t('app.ancs.sludgeEmptyingLastDate')}
                        >
                            <DateFieldWrapper />
                        </AvatarNoteReferenceField>

                        <FunctionField
                            label={t('app.ancs.maintenanceThreshold')}
                            // @ts-ignore
                            render={(record: Avatar) => <ShowLastNoteIcon record={record} tag={MAINTENANCE} />}
                        />

                        <FunctionField
                            label={t('app.ancs.sludgeEmptyingThreshold')}
                            // @ts-ignore
                            render={(record: Avatar) => <ShowLastNoteIcon record={record} tag={SLUDGE_EMPTYING} />}
                        />

                        {enabledColumns.currentState && (
                            <FunctionField
                                sortBy={'currentNode.name'}
                                source={'currentNode.name'}
                                label={t('app.ancs.nodeCurrentState')}
                                // @ts-ignore
                                render={(record: Avatar) => record.currentNode?.name?.toUpperCase()}
                            />
                        )}

                        {enabledColumns.currentState && (
                            <FunctionField
                                sortBy={'currentNode.dateOfTakesPlace'}
                                source={'currentNode.dateOfTakesPlace'}
                                label={t('app.ancs.nodeDateOfTakesPlace')}
                                // @ts-ignore
                                render={(record: Avatar) => toFormatDate(record.currentNode.dateOfTakesPlace)}
                            />
                        )}

                        {enabledColumns.processingCapacity && (
                            <FunctionField
                                label={t('app.ancs.processingCapacity')}
                                // @ts-ignore
                                render={(record: Avatar) => {
                                    const id =
                                        typeof record.metadataAvatarType === 'string'
                                            ? record.metadataAvatarType
                                            : record.metadataAvatarType.id;
                                    return (
                                        <MetadataAvatarTypeProperties
                                            metadataAvatarType={id}
                                            name='processingCapacity'
                                        />
                                    );
                                }}
                            />
                        )}

                        {enabledColumns.sludgeStorageVolume && (
                            <FunctionField
                                label={t('app.ancs.sludgeStorageVolume')}
                                // @ts-ignore
                                render={(record: Avatar) => {
                                    const id =
                                        typeof record.metadataAvatarType === 'string'
                                            ? record.metadataAvatarType
                                            : record.metadataAvatarType.id;
                                    return (
                                        <MetadataAvatarTypeProperties
                                            metadataAvatarType={id}
                                            name='sludgeStorageVolume'
                                        />
                                    );
                                }}
                            />
                        )}

                        {enabledColumns.totalUsefulVolume && (
                            <FunctionField
                                label={t('app.ancs.totalUsefulVolume')}
                                // @ts-ignore
                                render={(record: Avatar) => {
                                    const id =
                                        typeof record.metadataAvatarType === 'string'
                                            ? record.metadataAvatarType
                                            : record.metadataAvatarType.id;
                                    return (
                                        <MetadataAvatarTypeProperties
                                            metadataAvatarType={id}
                                            name='totalUsefulVolume'
                                        />
                                    );
                                }}
                            />
                        )}

                        {enabledColumns.model && (
                            <FunctionField
                                label={t('app.ancs.model')}
                                // @ts-ignore
                                render={(record: Avatar) => {
                                    const id =
                                        typeof record.metadataAvatarType === 'string'
                                            ? record.metadataAvatarType
                                            : record.metadataAvatarType.id;
                                    return <MetadataAvatarTypePropertiesBatched metadataAvatarType={id} name='model' />;
                                }}
                            />
                        )}

                        <FunctionField
                            label={t('app.ancs.accessToFinger')}
                            // @ts-ignore
                            render={(record: Avatar) => (
                                <IconButton onClick={() => simulateFingerMc(record)}>
                                    <Tooltip title={t('app.ancs.accessToFinger')}>
                                        <PhoneAndroidIcon />
                                    </Tooltip>
                                </IconButton>
                            )}
                        />

                        {hasRoles(['ROLE_RKT_PROCESSUS_SPECIFIQUE_WEBAPP_DASHBORD'], permissions || []) && (
                            <FunctionField
                                label={t('app.ancs.vueSpecifique')}
                                // @ts-ignore
                                render={(record: Avatar) => (
                                    <IconButton onClick={() => openBackofficeForms(record)}>
                                        <Tooltip title={t('app.ancs.vueSpecifique')}>
                                            <AssignmentIcon />
                                        </Tooltip>
                                    </IconButton>
                                )}
                            />
                        )}
                    </DataGridCustom>

                    {panelOpen && <RightInfoDrawer open={panelOpen} onClose={handleClosePanel} selected={selected} />}
                </Box>
            </List>
        </Box>
    );
};

export default TableView;
